import React, { useMemo, useState } from 'react';
import { ArrowUpRight, Bot, MessageCircle, Send, ShieldCheck, Sparkles, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CATEGORIES, PRODUCTS } from '../data/mock';
import ProductImage from './ProductImage';

const INITIAL_MESSAGE = {
  id: 'welcome',
  role: 'assistant',
  text: 'Cześć! Jestem pomocnikiem zakupowym AI. Mogę porozmawiać o katalogu, pomóc odnaleźć produkt i wyjaśnić proces kontaktu z obsługą.'
};

const QUICK_QUESTIONS = [
  'Jak zamówić?',
  'Pomóż mi znaleźć produkt z kategorii peptydy',
  'Jakie są ogólne ryzyka i skutki uboczne?'
];

function ProductPreview({ product }) {
  const category = CATEGORIES.find((item) => item.id === product.category)?.name || product.category;

  return (
    <Link
      to={`/produkt/${product.id}`}
      className="mt-2 flex items-center gap-3 rounded-xl border border-sky-100 bg-sky-50/70 p-2.5 text-left transition hover:border-sky-300 hover:bg-sky-50"
    >
      <ProductImage src={product.image} alt={product.name} name={product.name} color={product.color} className="h-14 w-14 rounded-lg bg-white object-contain text-[8px]" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-slate-900">{product.name}</span>
        <span className="mt-0.5 block text-xs text-slate-500">{category} · {product.dosage}</span>
        <span className="mt-1 block text-sm font-bold text-blue-700">{product.price.toFixed(2)} zł</span>
      </span>
      <ArrowUpRight className="h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
    </Link>
  );
}

function AiSupportWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [isLoading, setIsLoading] = useState(false);

  const catalog = useMemo(
    () => PRODUCTS.map((product) => ({
      name: product.name,
      category: CATEGORIES.find((category) => category.id === product.category)?.name || product.category,
      variant: product.dosage,
      price: product.price,
      summary: product.short
    })),
    []
  );

  const productsByName = useMemo(
    () => new Map(PRODUCTS.map((product) => [product.name, product])),
    []
  );

  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;

    const userMessage = { id: `user-${Date.now()}`, role: 'user', text: trimmed };
    const pendingId = `assistant-${Date.now()}`;
    const conversation = [...messages, userMessage];

    setMessages([
      ...conversation,
      { id: pendingId, role: 'assistant', text: 'Już sprawdzam — chwila…', pending: true }
    ]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: conversation.map(({ role, text: content }) => ({ role, content })),
          catalog
        })
      });
      const data = await response.json();

      if (!response.ok || !data.content) {
        throw new Error(data.error || 'Nie udało się uzyskać odpowiedzi.');
      }

      const products = (Array.isArray(data.products) ? data.products : [])
        .map((item) => productsByName.get(item?.name))
        .filter(Boolean);

      setMessages((current) => current.map((message) => (
        message.id === pendingId
          ? { id: pendingId, role: 'assistant', text: data.content, products }
          : message
      )));
    } catch (error) {
      setMessages((current) => current.map((message) => (
        message.id === pendingId
          ? {
              id: pendingId,
              role: 'assistant',
              text: 'Pomocnik jest chwilowo niedostępny. Spróbuj ponownie za moment lub skontaktuj się z obsługą przez Telegram.'
            }
          : message
      )));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage(input);
  };

  return (
    <div className="fixed bottom-5 right-5 z-[70] font-sans">
      {open && (
        <section
          aria-label="Pomocnik zakupowy AI"
          className="mb-3 flex w-[calc(100vw-2.5rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-sky-100 bg-white shadow-2xl shadow-slate-900/20"
        >
          <div className="flex items-start justify-between bg-gradient-to-br from-sky-600 to-blue-700 px-5 py-4 text-white">
            <div className="flex gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/15">
                <Bot className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="flex items-center gap-1 text-sm font-semibold"><Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> Pomocnik zakupowy AI</p>
                <p className="mt-0.5 text-xs text-sky-100">Darmowa rozmowa o katalogu</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-xl p-2 text-white transition hover:bg-white/15"
              aria-label="Zamknij pomocnika"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <div aria-live="polite" className="max-h-96 space-y-3 overflow-y-auto bg-slate-50 px-4 py-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`max-w-[92%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  message.role === 'user'
                    ? 'ml-auto bg-blue-600 text-white'
                    : 'bg-white text-slate-700 shadow-sm ring-1 ring-slate-100'
                } ${message.pending ? 'animate-pulse' : ''}`}
              >
                <p className="whitespace-pre-wrap">{message.text}</p>
                {message.products?.map((product) => <ProductPreview key={product.id} product={product} />)}
              </div>
            ))}
          </div>

          {messages.length === 1 && (
            <div className="flex flex-wrap gap-2 border-t border-slate-100 px-4 py-3">
              {QUICK_QUESTIONS.map((question) => (
                <button
                  key={question}
                  type="button"
                  onClick={() => sendMessage(question)}
                  disabled={isLoading}
                  className="rounded-full border border-sky-200 px-3 py-1.5 text-xs font-medium text-sky-700 transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {question}
                </button>
              ))}
            </div>
          )}

          <div className="border-t border-slate-100 px-4 py-3">
            <p className="mb-2 flex items-center gap-1.5 text-[11px] leading-relaxed text-slate-500">
              <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-sky-600" aria-hidden="true" />
              Informacje edukacyjne i katalogowe — decyzje medyczne zawsze konsultuj ze specjalistą.
            </p>
            <form onSubmit={handleSubmit} className="flex gap-2">
              <label className="sr-only" htmlFor="ai-support-message">Wiadomość do pomocnika</label>
              <input
                id="ai-support-message"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="Np. Jak zamówić?"
                className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-500 focus:bg-white focus:ring-2 focus:ring-sky-100"
              />
              <button
                type="submit"
                className="rounded-xl bg-blue-600 p-2.5 text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!input.trim() || isLoading}
                aria-label="Wyślij wiadomość"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
              </button>
            </form>
          </div>
        </section>
      )}

      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-label={open ? 'Zamknij pomocnika zakupowego AI' : 'Otwórz pomocnika zakupowego AI'}
        className="group flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-blue-700 text-white shadow-xl shadow-blue-600/30 transition hover:scale-105 hover:shadow-blue-600/45 focus:outline-none focus:ring-4 focus:ring-sky-200"
      >
        {open ? <X className="h-6 w-6" aria-hidden="true" /> : <MessageCircle className="h-6 w-6" aria-hidden="true" />}
      </button>
    </div>
  );
}

export default AiSupportWidget;
